import { Button } from "@chakra-ui/react";
import pptxgen from "pptxgenjs";
import { useState } from "react";

import {
  getAllPivotTablesForSurvey,
  findQuestionPivotByIdOrText,
  extractGenderAgeGroups,
  formatGroupedDataToChartWithAdults,
  getCurrentDateFormatted,
  getUniqueMediaItems,
  buildDescriptorSets,
  computeUniversalLabels,
  transformPlatformDataToStreamData,
  buildAgeOrdered,
  getQuestionResponseByIdOrText,
  transformPlatformUseToMediaFormat,
  transformDescriptorsToMediaFormat,
} from "./helpers";

import {
  addCoverSlides,
  addOverviewSummarySlide,
  addInterestDriversSlide,
  addUserChartSlide,
  addStreamPlatformSlide,
  addDemographicTableSlide,
  addTopDescriptorsSlides,
  addOpenEndedCommentsTables,
  addSingleMediaTargetSlide,
  addGenderTableSlide,
} from "./slide";

export function ComparePowerPointButton({
  media_item_meta,
  surveyData,
  pptData,
  interestDrivers,
  mediaSummaries,
  singleMedia = false,
}) {
  const [isLoading, setLoading] = useState(false);
  const POLL_INTERVAL_MS = 1000;

  const waitForDataToBeReady = () => {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        const ready =
          mediaSummaries?.length &&
          media_item_meta?.length &&
          mediaSummaries.length === media_item_meta.length;
        if (ready) {
          clearInterval(interval);
          resolve(true);
        }
      }, POLL_INTERVAL_MS);
    });
  };

  const handleExport = async () => {
    setLoading(true);

    try {
      await waitForDataToBeReady();

      if (singleMedia) {
        await exportSingleMedia();
      } else {
        const allPivots = getAllPivotTablesForSurvey(surveyData);
        const streamData = findQuestionPivotByIdOrText(
          allPivots,
          "platform-use",
          5
        ) || []

        const descriptorsData = findQuestionPivotByIdOrText(
          allPivots,
          "Based on the ad you viewed, please select the words and phrases that you think best describe this show. Please select all that apply.",
          5
        ) || []
        const genderAgeGroups = extractGenderAgeGroups(surveyData);
        let userChartData = []

        if (genderAgeGroups.length > 0) {
          userChartData =
            formatGroupedDataToChartWithAdults(genderAgeGroups);
        }

        await generatePowerPoint({
          media_item_meta: pptData?.meta_data,
          summaryData: mediaSummaries || [],
          interestDrivers,
          userChartData,
          streamData,
          descriptorsData,
          surveyDate: getCurrentDateFormatted(),
        });
      }
    } catch (error) {
      console.error("PowerPoint export failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const generatePowerPoint = async ({
    media_item_meta,
    summaryData,
    interestDrivers,
    userChartData,
    streamData,
    descriptorsData,
    surveyDate,
  }) => {
    try {
      if (!media_item_meta || media_item_meta.length === 0) {
        throw new Error("Missing media metadata");
      }

      const pres = new pptxgen();
      const slideWidth = 10;
      pres.defineLayout({ name: "DEFAULT", width: slideWidth, height: 10 });
      pres.layout = "DEFAULT";

      const uniquePromos = await getUniqueMediaItems(media_item_meta);

      await addCoverSlides(pres, uniquePromos, slideWidth);
      await addOverviewSummarySlide(
        pres,
        uniquePromos,
        summaryData,
        surveyDate,
        slideWidth
      );
      await addInterestDriversSlide(
        pres,
        uniquePromos,
        interestDrivers,
        surveyDate,
        slideWidth
      );

      if (!singleMedia) {
        const ageOrdered = buildAgeOrdered(userChartData || []);
        await addUserChartSlide(
          pres,
          uniquePromos,
          userChartData,
          surveyDate,
          slideWidth,
          ageOrdered
        );
      } else {
        await addSingleMediaTargetSlide(
          pres,
          uniquePromos,
          userChartData,
          slideWidth,
          surveyDate
        );
      }

      if (streamData?.length > 0) {
        const formattedStreamData =
          transformPlatformDataToStreamData(streamData);
        await addStreamPlatformSlide(
          pres,
          uniquePromos,
          surveyDate,
          formattedStreamData,
          slideWidth
        );
      }

      if (!singleMedia) {
        if (userChartData.length > 0) {
          await addDemographicTableSlide(
            pres,
            userChartData,
            uniquePromos,
            surveyDate,
            slideWidth
          );
        }

      } else {
        await addGenderTableSlide(
          pres,
          uniquePromos,
          userChartData,
          slideWidth,
          surveyDate
        );
      }
      if (descriptorsData?.length > 0) {
        const descriptorSets = buildDescriptorSets(descriptorsData, 5);
        const universalLabels = computeUniversalLabels(descriptorSets);

        await addTopDescriptorsSlides(
          pres,
          uniquePromos,
          descriptorSets,
          universalLabels,
          surveyDate,
          slideWidth
        );
      }

      await pres.writeFile("Survey");
    } catch (err) {
      console.error("Error generating PowerPoint:", err);
    }
  };

  const exportSingleMedia = async () => {
    try {
      if (!surveyData) {
        throw new Error("No survey data available for export.");
      }

      const streamData = getQuestionResponseByIdOrText(
        surveyData,
        "platform-use",
        5
      );
      const result = transformPlatformUseToMediaFormat(
        streamData,
        "Hype - Female 35-49"
      );

      const descriptorsData = getQuestionResponseByIdOrText(
        surveyData,
        "Based on the ad you viewed, please select the words and phrases that you think best describe this show. Please select all that apply.",
        5
      );
      const descriptorsResult = transformDescriptorsToMediaFormat(
        descriptorsData,
        "Descriptors - Female 18-34"
      );

      const userChart = getQuestionResponseByIdOrText(
        surveyData,
        "Who do you think would be most interested in watching this show?",
        5
      );

      await generatePowerPoint({
        media_item_meta: pptData?.meta_data,
        summaryData: mediaSummaries || [],
        interestDrivers,
        streamData: result,
        descriptorsData: descriptorsResult,
        surveyDate: getCurrentDateFormatted(),
        userChartData: userChart?.data || [],
      });
    } catch (err) {
      console.error("Error exporting single media:", err);
    }
  };

  return (
    <Button onClick={handleExport} isLoading={isLoading} colorScheme="blue">
      Export to PowerPoint
    </Button>
  );
}
